import { type SupportedCountryCode } from '@zep-us/i18n';
import { Button, ICON_COLOR } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import { CheckSmallIcon } from '@/icons';
import { type CategorySelectorState } from '../../category-selector/hooks/useCategorySelector';
import { PublisherMobileSelection } from '../../publisher-category-selector';
import { GradeMobileSelection, SchoolTypeMobileSelection, SemesterMobileSelection } from '../../school-category-selector';
import { SubjectCategoryMobileSection } from '../../subject-category-selector';
import { SaveForNextQuizCheckbox } from './SaveForNextQuizCheckbox';

/**
 * CategoryInfoView의 모바일 레이아웃
 *
 * 각 카테고리별 컴포넌트를 조합하여 세로 스택 형태로 카테고리 정보를 입력받습니다.
 * 각 컴포넌트는 내부적으로 표시 조건을 관리하고 조건에 맞지 않으면 null을 반환합니다.
 * - 학교급: SchoolTypeMobileSelection에서 관리
 * - 학년: GradeMobileSelection에서 조건 관리
 * - 학기: SemesterMobileSelection에서 조건 관리 (KR 사용자만)
 * - 과목 + 학습 분야: SubjectCategoryMobileSection에서 관리
 * - 출판사: PublisherMobileSelection에서 조건 관리
 */
const CategoryInfoMobileView = ({
  categoryInfo,
  onSubmit,
  userCountryCode
}: {
  categoryInfo: CategorySelectorState;
  onSubmit: () => void;
  userCountryCode: SupportedCountryCode;
}) => {
  const {
    t
  } = useTranslation(['build']);
  return <div className="flex h-full min-h-0 flex-col px-md" data-sentry-component="CategoryInfoMobileView" data-sentry-source-file="CategoryInfoMobileView.tsx">
      {/* 폼 콘텐츠 - 세로 스택 레이아웃 */}
      <div className="flex min-h-0 flex-1 flex-col gap-md overflow-y-auto py-md">
        {/* 학교급 (내부에서 표시 조건 관리) */}
        <SchoolTypeMobileSelection categoryInfo={categoryInfo} data-sentry-element="SchoolTypeMobileSelection" data-sentry-source-file="CategoryInfoMobileView.tsx" />

        {/* 학년 + 학기 나란히 배치 (각각 내부에서 표시 조건 관리) */}
        <div className="flex gap-xs">
          <GradeMobileSelection categoryInfo={categoryInfo} data-sentry-element="GradeMobileSelection" data-sentry-source-file="CategoryInfoMobileView.tsx" />

          <SemesterMobileSelection categoryInfo={categoryInfo} userCountryCode={userCountryCode} data-sentry-element="SemesterMobileSelection" data-sentry-source-file="CategoryInfoMobileView.tsx" />
        </div>

        {/* 과목 + 학습 분야 (내부에서 표시 조건 관리) */}
        <SubjectCategoryMobileSection categoryInfo={categoryInfo} data-sentry-element="SubjectCategoryMobileSection" data-sentry-source-file="CategoryInfoMobileView.tsx" />

        {/* 출판사 (내부에서 표시 조건 및 구분선 관리) */}
        <PublisherMobileSelection categoryInfo={categoryInfo} data-sentry-element="PublisherMobileSelection" data-sentry-source-file="CategoryInfoMobileView.tsx" />
      </div>
      <div className="pt-3xs">
        <SaveForNextQuizCheckbox checked={categoryInfo.isLastSchoolInfoSaved} onChange={checked => categoryInfo.setIsLastSchoolInfoSaved(checked)} data-sentry-element="SaveForNextQuizCheckbox" data-sentry-source-file="CategoryInfoMobileView.tsx" />
      </div>

      {/* 하단 액션 버튼 */}
      <div className="flex flex-shrink-0 flex-col justify-between bg-default py-md">
        <Button variant="contained" intent="strong" size="md" onClick={onSubmit} leftSlot={<CheckSmallIcon size="md" fill={ICON_COLOR.inverse} />} className="w-full" data-sentry-element="Button" data-sentry-source-file="CategoryInfoMobileView.tsx">
          {t('build.category.schoolLevelSelectionComplete')}
        </Button>
      </div>
    </div>;
};
export { CategoryInfoMobileView };